/**
 * LabsChipInput — Feb 28, 2026
 *
 * Replaces the bulky single-textarea "Labs / Orders to Place" field with
 * a chip-row of labs the provider can add via a popover. Inside the
 * popover the user picks from a 2-column grid of common labs (CBC, CMP,
 * BMP, A1C, TSH, Lipid panel, UA, LFTs, Mg/Phos, PT/INR, Vit D, B12) so
 * 4-5 chips can be added in a single popover trip — much faster than
 * typing each one. A free-text "Other" field captures rare labs.
 *
 * Round-trips with the existing `labOrders` string state so nothing
 * upstream (server prompt builder, history serialization) needs to
 * change: parse on read, comma-join on write. Empty string = no chips.
 */
import React, { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { Input } from '../ui/input';
import { OrderChipRow } from './OrderChipRow';

const COMMON_LABS = [
  'CBC', 'CMP', 'BMP', 'A1C',
  'TSH', 'Lipid panel', 'UA', 'LFTs',
  'Mg', 'Phos', 'PT/INR', 'Vit D',
  'B12', 'Folate', 'Ferritin', 'TSH/T4',
];

/** Parse a labOrders string into a deduped array of lab tokens. */
export const parseLabOrders = (str) => {
  if (!str || typeof str !== 'string') return [];
  return str
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Join an array of lab tokens back into the comma-separated string
 *  that downstream serialization expects. */
export const joinLabOrders = (arr) => (Array.isArray(arr) ? arr : []).join(', ');

const LabsEditor = ({ onSave, onCancel, existing }) => {
  const [picked, setPicked] = useState({});
  const [other, setOther] = useState('');
  const existingSet = new Set((existing || []).map((s) => s.toLowerCase()));

  const toggle = (lab) => setPicked((p) => ({ ...p, [lab]: !p[lab] }));

  const handleSave = () => {
    const picks = COMMON_LABS.filter((l) => picked[l] && !existingSet.has(l.toLowerCase()));
    const extra = other.split(/[,;\n]/).map((s) => s.trim()).filter((s) => s && !existingSet.has(s.toLowerCase()));
    const all = [...picks, ...extra];
    if (all.length === 0) {
      onCancel && onCancel();
      return;
    }
    // Save one at a time so the parent's onAdd is called per lab —
    // simpler than batch APIs and the upstream handler dedupes.
    onSave(all);
  };

  const totalPicked = COMMON_LABS.filter((l) => picked[l]).length + other.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean).length;

  return (
    <div className="space-y-2" data-testid="labs-editor">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
          Common labs — tap multiple
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          {COMMON_LABS.map((lab) => {
            const already = existingSet.has(lab.toLowerCase());
            const on = !!picked[lab];
            return (
              <button
                key={lab}
                type="button"
                onClick={() => !already && toggle(lab)}
                disabled={already}
                className={`flex items-center gap-2 cursor-pointer text-left ${already ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90'}`}
                style={{ background: 'transparent', border: 'none', padding: 0 }}
                data-testid={`labs-check-${lab.toLowerCase().replace(/[\s/]/g, '-')}`}
              >
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 14, height: 14,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: on ? 'rgba(86,209,204,0.55)' : 'rgba(255,255,255,0.18)',
                    background: on ? 'rgba(86,209,204,0.16)' : 'transparent',
                    color: '#56d1cc',
                    fontSize: 10, fontWeight: 900, lineHeight: 1,
                  }}
                >
                  {on ? '✓' : ''}
                </span>
                <span className={`text-[11.5px] ${on ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                  {lab}{already ? ' (added)' : ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
          Other <span className="font-normal normal-case text-muted-foreground/60">— comma-separate</span>
        </div>
        <Input
          value={other}
          onChange={(e) => setOther(e.target.value)}
          placeholder="e.g., Lyme, EBV titer, ANA, anti-CCP"
          className="h-8 text-xs"
          data-testid="labs-other"
        />
      </div>
      <div className="flex justify-end gap-1.5 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground border border-border hover:border-border-hot"
          style={{ borderRadius: 3, background: 'transparent' }}
          data-testid="labs-cancel"
        >Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={totalPicked === 0}
          className="px-3 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderRadius: 3, background: '#56d1cc', color: '#0f1419', border: '1px solid #56d1cc' }}
          data-testid="labs-save"
        >
          {totalPicked > 0 ? `Add ${totalPicked} lab${totalPicked === 1 ? '' : 's'}` : 'Add'}
        </button>
      </div>
    </div>
  );
};

export const LabsChipInput = ({ labOrders, setLabOrders }) => {
  const items = parseLabOrders(labOrders);

  const handleAdd = (newLabs) => {
    // newLabs is an array; merge in (case-insensitive de-dup).
    const have = new Set(items.map((s) => s.toLowerCase()));
    const merged = [...items];
    (Array.isArray(newLabs) ? newLabs : [newLabs]).forEach((l) => {
      if (l && !have.has(l.toLowerCase())) {
        merged.push(l);
        have.add(l.toLowerCase());
      }
    });
    setLabOrders(joinLabOrders(merged));
  };

  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index);
    setLabOrders(joinLabOrders(next));
  };

  // Editing a single lab chip = rename — simple inline replace.
  const handleUpdate = (index, newName) => {
    const trimmed = (typeof newName === 'string' ? newName : newName?.name || '').trim();
    if (!trimmed) return;
    const next = items.map((l, i) => (i === index ? trimmed : l));
    setLabOrders(joinLabOrders(next));
  };

  return (
    <OrderChipRow
      items={items}
      renderChip={(lab) => ({ label: lab })}
      renderEditor={({ value, onSave, onCancel, mode }) => (
        mode === 'create'
          ? <LabsEditor onSave={handleAdd} onCancel={onCancel} existing={items} />
          : (
            <LabRenameEditor
              value={value}
              onSave={(newName) => { handleUpdate(items.indexOf(value), newName); onSave(newName); }}
              onCancel={onCancel}
            />
          )
      )}
      addLabel="Add lab"
      accent="teal"
      sectionTitle="Labs / Orders"
      sectionIcon={<FlaskConical className="w-3.5 h-3.5 text-teal-400" />}
      testIdPrefix="labs-chip"
      onAdd={() => { /* handled inside renderEditor for batch */ }}
      onRemove={handleRemove}
    />
  );
};

const LabRenameEditor = ({ value, onSave, onCancel }) => {
  const [name, setName] = useState(value || '');
  return (
    <div className="space-y-2" data-testid="lab-rename-editor">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Lab name</div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
        className="h-8 text-xs"
        data-testid="lab-rename-input"
        onKeyDown={(e) => { if (e.key === 'Enter') onSave(name.trim()); }}
      />
      <div className="flex justify-end gap-1.5 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground border border-border hover:border-border-hot"
          style={{ borderRadius: 3, background: 'transparent' }}
          data-testid="lab-rename-cancel"
        >Cancel</button>
        <button
          type="button"
          onClick={() => onSave(name.trim())}
          disabled={!name.trim()}
          className="px-3 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderRadius: 3, background: '#56d1cc', color: '#0f1419', border: '1px solid #56d1cc' }}
          data-testid="lab-rename-save"
        >Save</button>
      </div>
    </div>
  );
};

export default LabsChipInput;
